import type { CSSProperties, ReactNode } from 'react'
import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ImageWithSkeleton } from '@/features/editor/components/shared/ImageWithSkeleton'
import {
  DEFAULT_BG,
  DEFAULT_TITLE_COLOR,
  getCoverBaseColor,
  getCoverTone,
  getShadowColor,
  SHADOW_OFFSET,
} from './coverHelpers'
import { WebCoverBadge } from './WebCoverBadge'
import s from './webCover.module.css'

interface Props {
  coverType: 'type1' | 'type2'
  title: string
  gradient: string | null
  cardImage: string | null
  titleColor: string | null
  authorName?: string | null
  authorAvatar?: string | null
  viewsCount?: number
  /** Студия: что показать на месте картинки, пока её нет (зона загрузки). */
  placeholder?: ReactNode
  /** Сердечко избранного, как в вебе. В списке студии у плитки свои кнопки. */
  showHeart?: boolean
}

const offset = `${SHADOW_OFFSET * 100}%`

/** Тип 2: картинка в белой рамке, повёрнутой на градус, с цветной «тенью». */
const MediaGroup = ({ image, gradient }: { image: string | null; gradient: string | null }) => (
  <>
    <div
      className={s.mediaShadow}
      style={{
        backgroundColor: getShadowColor(gradient),
        transform: `translate(-${offset}, ${offset})`,
      }}
    />
    <div className={s.mediaFrame}>
      {image ? (
        <ImageWithSkeleton
          src={image}
          className={s.mediaImage}
          skeletonClassName={s.mediaSkeleton}
        />
      ) : (
        <div className={s.mediaPlaceholder} />
      )}
    </div>
  </>
)

/**
 * Карточка инструкции — порт 1:1 карточки веба (article-frontend
 * `InstructionCard` + `Type1Cover`/`Type2Cover`). Превью обещает автору ровно
 * то, что увидит читатель в списке гайдов.
 */
export const WebInstructionCard = ({
  coverType,
  title,
  gradient,
  cardImage,
  titleColor,
  authorName,
  authorAvatar,
  viewsCount,
  placeholder,
  showHeart = true,
}: Props) => {
  const isType2 = coverType === 'type2'
  const slot = !cardImage && placeholder ? <div className={s.slot}>{placeholder}</div> : null
  return (
    <article className={s.card}>
      <div
        className={cn(s.root, isType2 ? s.type2 : s.type1)}
        style={
          {
            background: gradient ?? DEFAULT_BG,
            '--cover-tint': getCoverBaseColor(gradient),
          } as CSSProperties
        }
      >
        {isType2 ? (
          <div className={s.imageOuter}>
            <div className={s.imageInner}>
              <MediaGroup image={cardImage} gradient={gradient} />
              {slot}
            </div>
          </div>
        ) : (
          <div className={s.imageWrap}>
            {/* Пока картинка едет (сразу после загрузки обложки — тоже), на её месте
                пульсирует заглушка, а не пустой градиент. */}
            {cardImage ? (
              <ImageWithSkeleton
                src={cardImage}
                className={s.image}
                skeletonClassName={s.imageSkeleton}
              />
            ) : (
              slot
            )}
          </div>
        )}
        <div className={s.body}>
          <h3
            className={cn(s.title, isType2 ? s.clamp2 : s.clamp3)}
            style={{ color: titleColor ?? DEFAULT_TITLE_COLOR }}
          >
            {title}
          </h3>
          <WebCoverBadge
            authorName={authorName}
            authorAvatar={authorAvatar}
            viewsCount={viewsCount}
            tone={getCoverTone(gradient)}
          />
        </div>
      </div>
      {showHeart ? (
        <div className={s.topActions} aria-hidden>
          <Heart className={s.heart} />
        </div>
      ) : null}
    </article>
  )
}
